import React, { ReactNode } from "react";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import { Checkbox } from "@progress/kendo-react-inputs";
import { TruncatedText } from "../truncatedText/TruncatedText";
import { FilterBar } from "../filterBar/FilterBar";
import FooterPanel, { FooterProgressBarProps } from "../../layout/FooterPanel";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { ActionButton, ActionGroup } from "../../interfaces/ActionButton";
import { useAccordionListPanel } from "./AccordionListPanelHook";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";

export interface AccordionItemData<T> {
  id: string;
  title: string;
  data: T;
}

export interface AccordionListPanelProps<T> {
  items: AccordionItemData<T>[];
  filteredItems: AccordionItemData<T>[];
  expanded: string[];
  searchQuery: string;
  actionGroups: ActionGroup[];
  // Callbacks
  onAccordionToggle: (event: any, data: any) => void;
  onFilter: (query: string) => void;
  // Rendering
  itemStart?: (item: AccordionItemData<T>) => ReactNode;
  itemEnd?: (item: AccordionItemData<T>) => ReactNode;
  itemBadge?: (item: AccordionItemData<T>) => ReactNode;
  itemContent: (item: AccordionItemData<T>, expanded: boolean) => ReactNode;
  // Footer
  footer?: ReactNode | ((ctx: { selectedIds: string[]; selectedCount: number }) => ReactNode);
  /**
   * Optional progress-bar strip forwarded as-is to the outer `FooterPanel` this component renders
   * around `footer` — only meaningful together with `footer`. Default: undefined (off).
   */
  footerProgressBar?: FooterProgressBarProps;
  // Empty state
  emptyContent?: ReactNode;
  // Messages
  noItemsFoundMessage?: string;
  withSelection?: boolean;
  showActionsOnHover?: boolean;
  /** Whether to show the search/filter bar. Default: true */
  withFilter?: boolean;
  /** Whether to render the divider between the header and the item content. Default: true */
  withContentDivider?: boolean;
  /** Position of the expand chevron. Default: "start" */
  expandIconPosition?: "start" | "end";
  /** Override styles on the outer wrapper div */
  style?: React.CSSProperties;
  /** Override styles on the accordion item title (TruncatedText) */
  headerStyle?: React.CSSProperties;
  /** Suffix for automation IDs (e.g. "tag", "rule"). When set, generates IDs on card, chevron, and buttons. */
  automationIdSuffix?: string;
}

export function AccordionListPanel<T>({
  items,
  filteredItems,
  expanded,
  searchQuery,
  actionGroups,
  onAccordionToggle,
  onFilter,
  itemStart,
  itemEnd,
  itemBadge,
  itemContent,
  footer,
  footerProgressBar,
  emptyContent,
  noItemsFoundMessage,
  withSelection = false,
  showActionsOnHover = false,
  withFilter = true,
  withContentDivider = true,
  expandIconPosition = "start",
  style,
  headerStyle,
  automationIdSuffix,
}: Readonly<AccordionListPanelProps<T>>) {
  const {
    hasItems,
    hasFilteredItems,
    isItemExpanded,
    isItemSelected,
    allVisibleSelected,
    selectedIds,
    selectedCount,
    toggleItem,
    toggleSelectAll,
    stop,
    css,
  } = useAccordionListPanel({ items, filteredItems, expanded, withSelection, showActionsOnHover });

  // Extracted from a nested ternary into its own function per Sonar's suggested fix (S3358).
  const renderLeadingContent = (item: AccordionItemData<T>, isSelected: boolean): ReactNode => {
    if (itemStart) {
      return (
        <span {...stop} style={{ marginRight: 8 }}>
          {itemStart(item)}
        </span>
      );
    }
    if (withSelection) {
      return (
        <Checkbox checked={isSelected} onChange={() => toggleItem(item.id)} style={{ marginRight: 8 }} {...stop} />
      );
    }
    return null;
  };

  // Extracted out of a 5-levels-deep nested map (S2004) to reset the nesting for its
  // onClick/onKeyDown handlers.
  //
  // Keeps `role="button"` instead of a real `<button>` (S6819): this renders inside
  // `AccordionHeader`'s own native `<button>` root, and a nested `<button>` here would be invalid
  // HTML and break the header's own click-to-expand behavior. Left unfixed on purpose.
  const renderActionButton = (btn: ActionButton, itemId: string, itemTitle: string): ReactNode => {
    return (
      <span
        key={btn.key}
        aria-label={automationIdSuffix ? generateIdentifier(itemTitle, `${btn.key}_${automationIdSuffix}`) : undefined}
        className={btn.icon}
        role="button"
        tabIndex={0}
        title={btn.title}
        style={btn.style}
        onClick={(e) => {
          e.stopPropagation();
          btn.onClick(itemId);
        }}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            btn.onClick(itemId);
          }
        }}
      />
    );
  };

  return (
    <div style={{ padding: "0 8px", ...style }}>
      <style>{css}</style>

      {!hasItems ? (
        emptyContent
      ) : (
        <>
          {withFilter && (
            <FilterBar
              onFilter={onFilter}
              searchTrigger="change"
              currentQuery={searchQuery}
              currentFilters={[]}
              filters={[]}
              itemsFound={filteredItems.length}
              searchTransform={(q) => q}
              styles={{
                root: {
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: 0,
                },
                info: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  marginRight: "8px",
                  padding: "0 0 0 8px",
                },
              }}
            />
          )}

          {withSelection && (
            <div style={{ padding: "8px 10px", display: "flex", gap: 8, alignItems: "center" }}>
              <Checkbox checked={allVisibleSelected} onChange={toggleSelectAll} />
              <span style={{ fontWeight: 600 }}>{formatMessage("cof.base.selectall")}</span>
            </div>
          )}

          <Accordion multiple collapsible openItems={expanded} onToggle={onAccordionToggle}>
            {!hasFilteredItems && (
              <div style={{ padding: 10, color: "#777" }}>{noItemsFoundMessage || "No items found"}</div>
            )}

            {filteredItems.map((item) => {
              const isSelected = isItemSelected(item.id);
              const isExpanded = isItemExpanded(item.id);

              return (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className={`alp-item ${isExpanded ? "alp-expanded" : ""}`}
                  data-expanded={isExpanded ? "true" : "false"}
                >
                  <AccordionHeader
                    size="small"
                    expandIconPosition={expandIconPosition}
                    button={
                      automationIdSuffix
                        ? { "aria-label": generateIdentifier(item.title, automationIdSuffix) }
                        : undefined
                    }
                  >
                    <div style={{ display: "flex", width: "100%", alignItems: "center", marginRight: "4px" }}>
                      {renderLeadingContent(item, isSelected)}

                      <div style={{ flex: 1, display: "flex", alignItems: "center", overflow: "hidden" }}>
                        <TruncatedText text={item.title} style={headerStyle} />
                        {itemBadge?.(item)}
                      </div>

                      {itemEnd && (
                        <div style={{ flex: "0 0 auto", marginLeft: 6 }} {...stop}>
                          {itemEnd(item)}
                        </div>
                      )}

                      {actionGroups.length > 0 && (
                        <div className="alp-actions">
                          {actionGroups.map((group, gi) => (
                            <React.Fragment key={group.key}>
                              {group.buttons.map((btn) => renderActionButton(btn, item.id, item.title))}
                              {gi < actionGroups.length - 1 && (
                                <span style={{ margin: "0 8px", color: "#B0BEC5" }}>|</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionHeader>

                  <AccordionPanel style={{ padding: 0 }}>
                    <div style={{ padding: "0 12px" }}>
                      {withContentDivider && <hr style={{ borderTop: "1px solid #B0BEC5", margin: "0 0 12px" }} />}
                      {itemContent(item, isExpanded)}
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </>
      )}

      {footer !== undefined && (
        <FooterPanel progressBar={footerProgressBar}>
          {typeof footer === "function" ? footer({ selectedIds, selectedCount }) : footer}
        </FooterPanel>
      )}
    </div>
  );
}

export default AccordionListPanel;
